import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  FilterAltOff as FilterAltOffIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { api, MyCallListAgent, MyCallListProject } from '../../../services/api';
import {
  ColumnHeadCell,
  ColumnMenuPopover,
  EmptyNote,
  InlineCommentCell,
  leadingNumber,
  matchesText,
  paTableContainerSx,
  SortDirection,
  whatsappButtonSx,
} from './shared';

type CallsSortKey = 'called' | 'agent' | 'mobile' | 'why';

interface CallsFilters {
  agent: string;
  mobile: string;
  why: string;
  /** 'PENDING' | 'CALLED'. Empty means no filter. */
  status: string[];
}

const NO_FILTERS: CallsFilters = { agent: '', mobile: '', why: '', status: [] };

const CALLS_COLUMN_LABELS: Record<CallsSortKey, string> = {
  called: 'Called',
  agent: 'Agent',
  mobile: 'Mobile',
  why: 'Why',
};

const STATUS_OPTIONS = [
  { key: 'PENDING', label: 'Pending' },
  { key: 'CALLED', label: 'Called' },
];

function callsColumnHasFilter(filters: CallsFilters, key: CallsSortKey): boolean {
  switch (key) {
    case 'agent':
      return Boolean(filters.agent.trim());
    case 'mobile':
      return Boolean(filters.mobile.trim());
    case 'why':
      return Boolean(filters.why.trim());
    case 'called':
      return filters.status.length > 0;
    default:
      return false;
  }
}

function callsFiltersAreEmpty(filters: CallsFilters): boolean {
  return (['called', 'agent', 'mobile', 'why'] as CallsSortKey[]).every(
    (key) => !callsColumnHasFilter(filters, key)
  );
}

/**
 * What a core team member sees: the agents their project's lead handed them,
 * the lead's brief on each, a WhatsApp shortcut with the message pre-filled,
 * and their own note back.
 */
const MyCallsTab: React.FC = () => {
  const [projects, setProjects] = useState<MyCallListProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [filters, setFilters] = useState<CallsFilters>(NO_FILTERS);
  const [sortConfig, setSortConfig] = useState<{
    column: CallsSortKey | null;
    direction: SortDirection;
  }>({ column: null, direction: 'default' });
  const [menuAnchor, setMenuAnchor] = useState<{
    element: HTMLElement;
    column: CallsSortKey;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getMyProjectActionTasks();
      setProjects(response.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load your list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Folds a server-confirmed row back into the cached list. */
  const applyState = useCallback(
    (
      taskId: number,
      patch: { calledAt: string | null; callRemark: string | null; linkSentCount: number; lastLinkSentAt: string | null }
    ) => {
      setProjects((prev) =>
        prev.map((project) => {
          const agents = project.agents.map((agent) =>
            agent.id === taskId ? { ...agent, ...patch } : agent
          );
          return { ...project, agents, pending: agents.filter((a) => !a.calledAt).length };
        })
      );
    },
    []
  );

  const handleToggleCalled = async (agent: MyCallListAgent, called: boolean) => {
    setBusyId(agent.id);
    try {
      applyState(agent.id, await api.setProjectActionCalled(agent.id, called));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update call status');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveRemark = async (taskId: number, callRemark: string) => {
    try {
      applyState(taskId, await api.setProjectActionRemark(taskId, callRemark || null));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save comment');
      await load();
    }
  };

  // The click is the only evidence a message was started — WhatsApp never
  // reports back — so it is logged as the link opening, not as delivery.
  const handleWhatsAppOpen = async (agent: MyCallListAgent) => {
    try {
      applyState(agent.id, await api.recordProjectActionLinkSent(agent.id));
    } catch {
      /* the chat still opened; a lost counter is not worth blocking on */
    }
  };

  const handleCopyMessage = async (taskId: number, message: string) => {
    try {
      await navigator.clipboard.writeText(message);
      setCopiedId(taskId);
      window.setTimeout(() => setCopiedId((id) => (id === taskId ? null : id)), 1500);
    } catch {
      /* clipboard unavailable — nothing useful to show */
    }
  };

  const setFilter = (patch: Partial<CallsFilters>) =>
    setFilters((prev) => ({ ...prev, ...patch }));
  const hasFilters = !callsFiltersAreEmpty(filters);

  const sortDirectionFor = (column: CallsSortKey): SortDirection =>
    sortConfig.column === column ? sortConfig.direction : 'default';

  const columnIsActive = (column: CallsSortKey): boolean =>
    callsColumnHasFilter(filters, column) || sortDirectionFor(column) !== 'default';

  const clearColumnFilter = (column: CallsSortKey) => {
    if (column === 'agent') setFilter({ agent: '' });
    else if (column === 'mobile') setFilter({ mobile: '' });
    else if (column === 'why') setFilter({ why: '' });
    else setFilter({ status: [] });
  };

  // One filter/sort across every project's table, and a project whose rows all
  // filter out drops away rather than leaving an empty heading behind.
  const visibleProjects = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const sortColumn =
      sortConfig.column && sortConfig.direction !== 'default' ? sortConfig.column : null;

    return projects
      .map((project) => {
        const agents = project.agents.filter((agent) => {
          if (!matchesText(`${agent.agentName || ''} ${agent.agentCEANumber}`, filters.agent)) {
            return false;
          }
          if (!matchesText(agent.agentMobile, filters.mobile)) return false;
          if (!matchesText(agent.remarks, filters.why)) return false;
          if (filters.status.length > 0) {
            if (!filters.status.includes(agent.calledAt ? 'CALLED' : 'PENDING')) return false;
          }
          return true;
        });

        const sorted = sortColumn
          ? [...agents].sort((a, b) => {
              switch (sortColumn) {
                case 'called': {
                  const va = a.calledAt ? 1 : 0;
                  const vb = b.calledAt ? 1 : 0;
                  if (va !== vb) return (va - vb) * dir;
                  break;
                }
                case 'mobile':
                  if ((a.agentMobile || '') !== (b.agentMobile || '')) {
                    return (a.agentMobile || '').localeCompare(b.agentMobile || '') * dir;
                  }
                  break;
                case 'why': {
                  const na = leadingNumber(a.remarks);
                  const nb = leadingNumber(b.remarks);
                  if (na === null && nb === null) break;
                  if (na === null) return 1;
                  if (nb === null) return -1;
                  if (na !== nb) return (na - nb) * dir;
                  break;
                }
                default:
                  break;
              }
              const na = a.agentName || a.agentCEANumber;
              const nb = b.agentName || b.agentCEANumber;
              return na.localeCompare(nb) * (sortColumn === 'agent' ? dir : 1);
            })
          : agents;

        return { ...project, agents: sorted, matched: sorted.length, all: project.agents.length };
      })
      .filter((project) => project.agents.length > 0);
  }, [projects, filters, sortConfig]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {hasFilters && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ color: 'rgb(var(--color-text-rgb) / 0.6)' }}>
            {`Showing ${visibleProjects.reduce((n, p) => n + p.agents.length, 0)} of ${projects.reduce(
              (n, p) => n + p.agents.length,
              0
            )} agents`}
          </Typography>
          <Button
            size="small"
            startIcon={<FilterAltOffIcon />}
            onClick={() => setFilters(NO_FILTERS)}
            sx={{ textTransform: 'none' }}
          >
            Clear filters
          </Button>
        </Stack>
      )}
      {projects.length === 0 ? (
        <EmptyNote>No agents have been assigned to you to call.</EmptyNote>
      ) : visibleProjects.length === 0 ? (
        <EmptyNote>No agents match these filters.</EmptyNote>
      ) : (
        <Stack spacing={3}>
          {visibleProjects.map((project) => (
            <Box key={project.projectId}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                {project.projectName || project.projectId}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1, color: 'rgb(var(--color-text-rgb) / 0.6)' }}
                >
                  {hasFilters
                    ? `${project.matched} of ${project.all} shown`
                    : `${project.pending} of ${project.agents.length} still to call`}
                </Typography>
              </Typography>
              <TableContainer sx={paTableContainerSx}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <ColumnHeadCell
                        label="Called"
                        align="center"
                        active={columnIsActive('called')}
                        onOpenMenu={(element) => setMenuAnchor({ element, column: 'called' })}
                      />
                      <ColumnHeadCell
                        label="Agent"
                        minWidth={180}
                        active={columnIsActive('agent')}
                        onOpenMenu={(element) => setMenuAnchor({ element, column: 'agent' })}
                      />
                      <ColumnHeadCell
                        label="Mobile"
                        minWidth={110}
                        active={columnIsActive('mobile')}
                        onOpenMenu={(element) => setMenuAnchor({ element, column: 'mobile' })}
                      />
                      <ColumnHeadCell
                        label="Why"
                        minWidth={110}
                        active={columnIsActive('why')}
                        onOpenMenu={(element) => setMenuAnchor({ element, column: 'why' })}
                      />
                      <ColumnHeadCell label="Lead's note" minWidth={180} />
                      <ColumnHeadCell label="My comment" minWidth={200} />
                      <ColumnHeadCell label="WhatsApp" align="center" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {project.agents.map((agent) => {
                      const called = Boolean(agent.calledAt);
                      return (
                        <TableRow
                          key={agent.id}
                          hover
                          // A tint, never opacity: opacity on the row would drag
                          // the WhatsApp button down with it (a child cannot undo
                          // a parent's opacity) and make it read as disabled.
                          sx={{
                            bgcolor: called ? 'rgb(var(--color-success-rgb) / 0.08)' : 'transparent',
                            transition: 'background-color 150ms ease',
                          }}
                        >
                          <TableCell align="center">
                            <Checkbox
                              checked={called}
                              disabled={busyId === agent.id}
                              onChange={(e) => handleToggleCalled(agent, e.target.checked)}
                              sx={{ '&.Mui-checked': { color: 'var(--color-success)' } }}
                              inputProps={{
                                'aria-label': `Mark ${agent.agentName || agent.agentCEANumber} as called`,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {agent.agentName || agent.agentCEANumber}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: 'rgb(var(--color-text-rgb) / 0.55)' }}
                            >
                              {agent.agentCEANumber}
                              {agent.listTitle ? ` · ${agent.listTitle}` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {agent.agentMobile || '—'}
                          </TableCell>
                          <TableCell>
                            {agent.remarks ? (
                              <Chip size="small" label={agent.remarks} variant="outlined" />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 180 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: agent.leadRemark
                                  ? 'var(--color-text-strong)'
                                  : 'rgb(var(--color-text-rgb) / 0.45)',
                              }}
                            >
                              {agent.leadRemark || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            <InlineCommentCell
                              value={agent.callRemark}
                              placeholder="Add a comment"
                              ariaLabel={`Comment on the call to ${agent.agentName || agent.agentCEANumber}`}
                              onSave={(next) => handleSaveRemark(agent.id, next)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack spacing={0.5} alignItems="flex-end">
                              {agent.whatsappLink ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<WhatsAppIcon />}
                                  href={agent.whatsappLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => handleWhatsAppOpen(agent)}
                                  sx={whatsappButtonSx}
                                >
                                  Chat
                                </Button>
                              ) : (
                                <Tooltip title="No usable mobile number on file — copy the message instead">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<ContentCopyIcon />}
                                      onClick={() => handleCopyMessage(agent.id, agent.whatsappMessage)}
                                      sx={{ whiteSpace: 'nowrap' }}
                                    >
                                      {copiedId === agent.id ? 'Copied' : 'Copy message'}
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                              {agent.linkSentCount > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'rgb(var(--color-text-rgb) / 0.5)' }}
                                >
                                  {`sent ${agent.linkSentCount}×`}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </Stack>
      )}

      <ColumnMenuPopover
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor?.element ?? null}
        label={menuAnchor ? CALLS_COLUMN_LABELS[menuAnchor.column] : ''}
        direction={menuAnchor ? sortDirectionFor(menuAnchor.column) : 'default'}
        onDirectionChange={(direction) => {
          if (!menuAnchor) return;
          setSortConfig(
            direction === 'default'
              ? { column: null, direction: 'default' }
              : { column: menuAnchor.column, direction }
          );
        }}
        onClose={() => setMenuAnchor(null)}
        onClear={() => menuAnchor && clearColumnFilter(menuAnchor.column)}
        clearDisabled={!menuAnchor || !callsColumnHasFilter(filters, menuAnchor.column)}
      >
        {menuAnchor &&
          (menuAnchor.column === 'called' ? (
            <Autocomplete
              multiple
              size="small"
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.filter((option) => filters.status.includes(option.key))}
              onChange={(_e, value) => setFilter({ status: value.map((v) => v.key) })}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              renderInput={(params) => <TextField {...params} placeholder="Any" label="Status" />}
            />
          ) : (
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={menuAnchor.column === 'agent' ? 'Name or CEA' : 'Contains…'}
              value={
                menuAnchor.column === 'agent'
                  ? filters.agent
                  : menuAnchor.column === 'mobile'
                  ? filters.mobile
                  : filters.why
              }
              onChange={(e) =>
                setFilter(
                  menuAnchor.column === 'agent'
                    ? { agent: e.target.value }
                    : menuAnchor.column === 'mobile'
                    ? { mobile: e.target.value }
                    : { why: e.target.value }
                )
              }
            />
          ))}
      </ColumnMenuPopover>
    </>
  );
};

export default MyCallsTab;
